import { NoddiDateFormats, noddiFormatDate, subMonths } from '../date/date';

export const IntervalValues = {
  THIS_SEASON: 'this_season',
  PREVIOUS_SEASON: 'previous_season',
  THIS_YEAR: 'this_year',
  THIS_MONTH: 'this_month',
  THIS_WEEK: 'this_week',
  TODAY: 'today',
  LAST_7D: 'last_7d',
  LAST_14D: 'last_14d',
  LAST_30D: 'last_30d',
  LAST_3M: 'last_3m',
  NEXT_14D: 'next_14d',
  NEXT_30D: 'next_30d',
  NEXT_60D: 'next_60d'
} as const;

export const intervalTabItems: { label: string; value: IntervalValue }[] = [
  { label: 'This year', value: IntervalValues.THIS_YEAR },
  { label: 'This month', value: IntervalValues.THIS_MONTH },
  { label: 'This week', value: IntervalValues.THIS_WEEK },
  { label: 'Today', value: IntervalValues.TODAY },
  { label: 'Last 7 days', value: IntervalValues.LAST_7D },
  { label: 'Last 14 days', value: IntervalValues.LAST_14D },
  { label: 'Last 30 days', value: IntervalValues.LAST_30D },
  { label: 'Last 3 months', value: IntervalValues.LAST_3M }
] as const;

export const forwardIntervalTabItems: { label: string; value: IntervalValue }[] = [
  { label: 'Next 14 days', value: IntervalValues.NEXT_14D },
  { label: 'Next 30 days', value: IntervalValues.NEXT_30D },
  { label: 'Next 60 days', value: IntervalValues.NEXT_60D },
  { label: 'This month', value: IntervalValues.THIS_MONTH },
  { label: 'This year', value: IntervalValues.THIS_YEAR },
  { label: 'Today', value: IntervalValues.TODAY }
] as const;

export type IntervalValue = (typeof IntervalValues)[keyof typeof IntervalValues];

export interface DateRange {
  from: string;
  to: string;
}

/**
 * Converts interval values to from and to dates formatted as ISO dates
 * @param interval - The interval value to convert
 * @returns Object with from and to dates formatted as ISO strings
 */
export const intervalToDateRange = (interval: IntervalValue): DateRange => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // eslint-disable-next-line no-restricted-syntax -- assigned per interval case in the switch
  let from: Date;
  // eslint-disable-next-line no-restricted-syntax -- assigned per interval case in the switch
  let to: Date;

  switch (interval) {
    case IntervalValues.THIS_SEASON:
    case IntervalValues.PREVIOUS_SEASON: {
      const seasonStartMonth = now.getMonth() < 6 ? 0 : 6;
      const startMonth = interval === IntervalValues.THIS_SEASON ? seasonStartMonth : seasonStartMonth - 6;
      from = new Date(now.getFullYear(), startMonth, 1);
      to = new Date(now.getFullYear(), startMonth + 6, 0);
      break;
    }

    case IntervalValues.THIS_YEAR:
      from = new Date(now.getFullYear(), 0, 1);
      to = new Date(now.getFullYear(), 11, 31);
      break;

    case IntervalValues.THIS_MONTH:
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
      break;

    case IntervalValues.THIS_WEEK: {
      const dayOfWeek = today.getDay();
      const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      from = new Date(today);
      from.setDate(today.getDate() - daysFromMonday);
      to = new Date(from);
      to.setDate(from.getDate() + 6); // End of week (Sunday)
      break;
    }

    case IntervalValues.TODAY:
      from = new Date(today);
      to = new Date(today);
      break;

    case IntervalValues.LAST_7D:
      from = new Date(today);
      from.setDate(today.getDate() - 6);
      to = today;
      break;

    case IntervalValues.LAST_14D:
      from = new Date(today);
      from.setDate(today.getDate() - 13);
      to = today;
      break;

    case IntervalValues.LAST_30D:
      from = new Date(today);
      from.setDate(today.getDate() - 29);
      to = today;
      break;

    case IntervalValues.LAST_3M:
      from = subMonths(today, 3) as Date;
      to = today;
      break;

    case IntervalValues.NEXT_14D:
      from = today;
      to = new Date(today);
      to.setDate(today.getDate() + 13);
      break;

    case IntervalValues.NEXT_30D:
      from = today;
      to = new Date(today);
      to.setDate(today.getDate() + 29);
      break;

    case IntervalValues.NEXT_60D:
      from = today;
      to = new Date(today);
      to.setDate(today.getDate() + 59);
      break;

    default:
      from = new Date(today);
      from.setDate(today.getDate() - 6);
      to = today;
  }

  return {
    from: noddiFormatDate(from, NoddiDateFormats.API_DATE),
    to: noddiFormatDate(to, NoddiDateFormats.API_DATE)
  };
};
